import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';
import type { Brand, Location } from '@traaker/shared';

interface BrandState {
  brands: Brand[];
  activeBrand: Brand | null;
  activeLocation: Location | null;
  loading: boolean;
  setActiveBrand: (brand: Brand) => void;
  setActiveLocation: (location: Location | null) => void;
  refreshBrands: () => Promise<void>;
}

const BrandContext = createContext<BrandState>({
  brands: [],
  activeBrand: null,
  activeLocation: null,
  loading: true,
  setActiveBrand: () => {},
  setActiveLocation: () => {},
  refreshBrands: async () => {},
});

const ACTIVE_BRAND_STORAGE_KEY = 'traaker.activeBrandId';

/**
 * `?brandId=` read once, at module load, from the URL that opened the app.
 *
 * Notification emails are rendered PER BRAND, so their CTAs deep-link with this
 * param — without it a 20-brand tenant clicking "voir le tableau de bord" from
 * brand B's report lands on whichever brand localStorage last held (reported
 * 2026-08-27). Read at module scope, not in a hook, so it survives the
 * `history.replaceState` below and cannot be re-applied by a later
 * `refreshBrands()` after the user has manually switched brands.
 */
const urlBrandId = (() => {
  if (typeof window === 'undefined') return null;
  const id = new URLSearchParams(window.location.search).get('brandId');
  if (!id) return null;
  // Strip it so the deep-link doesn't outlive the first load: with the param
  // still in the address bar, a reload after switching brands would silently
  // snap back to the emailed brand.
  const url = new URL(window.location.href);
  url.searchParams.delete('brandId');
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  return id;
})();

export function BrandProvider({ children }: { children: ReactNode }) {
  const { tenant } = useAuth();
  /** Consumed on the first load that actually resolves brands, then never again. */
  const pendingUrlBrandId = useRef<string | null>(urlBrandId);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [activeBrand, setActiveBrand] = useState<Brand | null>(null);
  const [activeLocation, setActiveLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(true);

  const loadBrands = useCallback(async () => {
    if (!tenant) {
      // No tenant resolved yet — we haven't loaded brands, so stay in the
      // loading state rather than reporting "loaded, zero brands". Otherwise
      // OnboardingGate would see loading=false + brands=[] during the
      // auth-resolution window and wrongly redirect a tenant that HAS brands.
      return;
    }
    try {
      const data = await api.get<Brand[]>('/brands');
      setBrands(data);
      // Re-bind the active brand to the fresh record so callers that trigger
      // refreshBrands (e.g. after validate / save strategy) see the new
      // strategyMeta / geoStrategyMeta values. On first load (activeBrand
      // is null) restore from localStorage so a page refresh keeps the
      // user's selection instead of snapping to data[0].
      if (data.length > 0) {
        const storedId =
          typeof window !== 'undefined'
            ? window.localStorage.getItem(ACTIVE_BRAND_STORAGE_KEY)
            : null;
        // `?brandId=` outranks both the in-memory selection and localStorage —
        // that's the whole point of a deep link. An id the tenant doesn't own
        // (deleted brand, wrong tenant) just falls through to the usual chain.
        const linked = pendingUrlBrandId.current
          ? data.find((b) => b.brandId === pendingUrlBrandId.current)
          : undefined;
        pendingUrlBrandId.current = null;
        const fresh =
          linked ||
          (activeBrand && data.find((b) => b.brandId === activeBrand.brandId)) ||
          (storedId ? data.find((b) => b.brandId === storedId) : undefined);
        if (fresh) {
          setActiveBrand(fresh);
          if (linked && typeof window !== 'undefined') {
            // Persist so navigating away from the landing page keeps the brand.
            window.localStorage.setItem(ACTIVE_BRAND_STORAGE_KEY, linked.brandId);
          }
        } else {
          setActiveBrand(data[0]);
          setActiveLocation(null);
        }
      } else {
        setActiveBrand(null);
        setActiveLocation(null);
      }
    } catch {
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, [tenant, activeBrand]);

  useEffect(() => {
    loadBrands();
  }, [tenant]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetActiveBrand = useCallback((brand: Brand) => {
    setActiveBrand(brand);
    setActiveLocation(null); // reset location on brand switch
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ACTIVE_BRAND_STORAGE_KEY, brand.brandId);
    }
  }, []);

  return (
    <BrandContext.Provider
      value={{
        brands,
        activeBrand,
        activeLocation,
        loading,
        setActiveBrand: handleSetActiveBrand,
        setActiveLocation,
        refreshBrands: loadBrands,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}
